import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import FileCopyIcon from "@mui/icons-material/FileCopy"; // Generic file icon
import ImageIcon from "@mui/icons-material/Image"; // Image icon
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"; // PDF icon
import DescriptionIcon from "@mui/icons-material/Description"; // Text/doc icon
import TableChartIcon from "@mui/icons-material/TableChart"; // Excel/spreadsheet icon
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  TextField,
  Autocomplete,
  Paper,
  Tooltip,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  Collapse,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { showLoadingSwal } from "../../components/loadingSwal";
import moment from "moment";
import { updateNote } from "../../api/notesApis/updateNote";
import Swal from "sweetalert2";
import { downloadNotesFile } from "../../api/notesApis/downloadNote";
const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  "& .MuiInputBase-root": {
    height: "45px", // Ekran boyutuna göre dinamik yükseklik
    padding: "0px 10px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.5rem", // Küçük ekranlarda yazı boyutunu küçültme
    },
  },
  "& .MuiInputBase-input": {
    padding: "0px 5px", // Padding değerini dinamik yapabiliriz
    [theme.breakpoints.down("sm")]: {
      padding: "0rem", // Küçük ekranlarda padding değerini küçültme
    },
  },
}));
// Styled Components
const StyledModal = styled(Modal)(({ theme }) => ({
  "& .MuiBox-root": {
    outline: "none",
    overflow: "hidden",
    maxHeight: "90vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[400],
      borderRadius: "4px",
      "&:hover": { backgroundColor: theme.palette.grey[600] },
    },
  },
}));

const ContentField = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowX: "hidden",
  display: "flex",
  alignItems: "center",
  wordBreak: "break-word",
  fontSize: "0.85rem",
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.2),
  backgroundColor: theme.palette.grey[50],
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.grey[100],
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#ffffff9a",
  borderRadius: "12px", // daha yuvarlak köşeler
  padding: "5px", // biraz iç boşluk
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)", // soft shadow
  transition: "all 0.25s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    backgroundColor: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", // daha belirgin shadow
  },
}));

const FieldLabel = styled("span")(({ theme }) => ({
  fontWeight: 400,
  padding: "0px 0px",
  fontSize: "0.9rem",
  color: theme.palette.text.primary,
  minWidth: "90px",
  display: "inline-block",
}));

export const NoteModal = ({
  isOpen,
  onClose,
  note,
  taxpayerList,
  workerList,
  search,
  workTypeOptions,
  workStatusOptions,
}) => {
  const [editMode, setEditMode] = useState({
    title: false,
    description: false,
    status: false,
    workerTitle: false,
    workType: false,
    taxpayer: false,
    expiresAt: false,
    files: false,
  });

  const [tempTitle, setTempTitle] = useState("");
  const [tempDescription, setTempDescription] = useState("");
  const [tempStatus, setTempStatus] = useState(null);
  const [tempWorkerTitle, setTempWorkerTitle] = useState(null);
  const [tempWorkType, setTempWorkType] = useState(null);
  const [tempTaxpayer, setTempTaxpayer] = useState(null);
  const [tempExpiresAt, setTempExpiresAt] = useState(null);
  const [tempFiles, setTempFiles] = useState([]);

  useEffect(() => {
    if (note) {
      setTempTitle(note.title || "");
      setTempDescription(note.description || "");
      setTempStatus(note.status || null);
      setTempWorkerTitle(note.workerTitle || null);
      setTempTaxpayer(note.taxpayer || null);
      setTempWorkType(note.workType || null);
      setTempExpiresAt(note.expiresAt ? moment(note.expiresAt) : null);
      setTempFiles(note.files || []); // Assuming note.files contains previously uploaded files
    }
  }, [note]);

  // File Upload Logic
  const onDrop = (acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        setTempFiles((prev) => [
          ...prev,
          { name: file.name, data: base64String },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };
  const [errorOpen, setErrorOpen] = useState(false);
  const onDropRejected = () => {
    setErrorOpen(true);
  };
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: 7 * 1024 * 1024, // 7 MB sınırı
    noDrag: true,
    noClick: true,
  });

  const handleDeleteFile = (indexToDelete) => {
    setTempFiles((prev) => prev.filter((_, index) => index !== indexToDelete));
  };
  const handleFileDrop = (acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        setTempFiles((prev) => [
          ...prev,
          { name: file.name, data: base64String },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };
  const startEdit = (field) =>
    setEditMode((prev) => ({ ...prev, [field]: true }));

  const cancelEdit = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: false }));
    if (field === "title") setTempTitle(note?.title || "");
    if (field === "description") setTempDescription(note?.description || "");
    if (field === "status") setTempStatus(note?.status || null);
    if (field === "workerTitle") setTempWorkerTitle(note?.workerTitle || null);
    if (field === "workType") setTempWorkType(note?.workType || null);
    if (field === "taxpayer") setTempTaxpayer(note?.taxpayer || null);
    if (field === "expiresAt")
      setTempExpiresAt(note?.expiresAt ? moment(note.expiresAt) : null);
    if (field === "files") setTempFiles(note?.files || []);
  };

  const saveField = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: false }));
    if (field === "files") setTempFiles(tempFiles); // Sync tempFiles to main files state on save
  };
  const MAX_FILE_SIZE = 7 * 1024 * 1024; // 7 MB
  const handleSave = async () => {
    for (const file of tempFiles) {
      if (file.size > MAX_FILE_SIZE) {
        Swal.fire(
          "Hata!",
          `"${file.name}" dosyası 7 MB'dan büyük. Lütfen daha küçük bir dosya yükleyin.`,
          "error"
        );
        return; // İşlemi durdur
      } else if (tempTitle.length === 0 && tempDescription.length === 0) {
        Swal.fire(
          "Hata!",
          `Başlık ve Açıklama aynı anda boş bırakılamaz.`,
          "error"
        );
        return; // İşlemi durdur
      }
    }
    const updatedNote = {
      ...note,
      title: tempTitle,
      description: tempDescription,
      status: tempStatus,
      workType: tempWorkType,
      workerId: Number(tempWorkerTitle) || undefined,
      taxpayerId: Number(tempTaxpayer) || undefined,
      expiresAt: tempExpiresAt ? moment(tempExpiresAt).endOf("day") : null,
      files: tempFiles || [], // Include files in the updated note
    };

    try {
      await updateNote(updatedNote);
      Swal.fire({
        icon: "success",
        title: "Başarılı!",
        text: "Kayıt başarıyla güncellendi.",
        showConfirmButton: false,
        timer: 1500,
        background: "#f8f9fa",
      });
      search();
      onClose();
      setEditMode({
        title: false,
        description: false,
        status: false,
        workerTitle: false,
        workType: false,
        taxpayer: false,
        expiresAt: false,
        files: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Hata!",
        text: "Kayıt güncelleme sırasında bir hata oluştu.",
        confirmButtonColor: "#3f51b5",
      });
    }
  };
  // Add this utility function outside the component to determine file type and icon
  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <ImageIcon fontSize="small" color="primary" sx={{ mr: 1 }} />;
      case "pdf":
        return (
          <PictureAsPdfIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
        );
      case "txt":
      case "doc":
      case "docx":
        return (
          <DescriptionIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
        );
      case "xls":
      case "xlsx":
        return (
          <TableChartIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
        );
      default:
        return <FileCopyIcon fontSize="small" color="primary" sx={{ mr: 1 }} />;
    }
  };
  const handleFileClick = async (driveId) => {
    try {
      // Yükleniyor bildirimi göster
      await showLoadingSwal();

      console.log("Download başlatılıyor...");
      // Dosyayı Drive'dan indir
      const file = await downloadNotesFile(driveId); // downloadFile fonksiyonunu çağırıyor
      console.log("Dosya alındı:", file);

      // Base64'ü Blob'a çevirme fonksiyonu
      const base64ToBlob = (base64, mimeType) => {
        const byteCharacters = atob(base64); // Base64'ü decode et
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
      };

      // Base64 verisini Blob'a çevir
      const blob = base64ToBlob(file.data.data, file.data.mimeType);

      // Blob'dan indirme URL'si oluştur
      const url = URL.createObjectURL(blob);

      // Dosyayı indirme işlemi için bir link oluştur
      const link = document.createElement("a");
      link.href = url;
      link.download = file.data.fileName; // Orijinal dosya adını kullanıyoruz
      document.body.appendChild(link);
      link.click();

      // Temizlik
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      // İşlem başarılı olduğunda SweetAlert'i kapat
      Swal.close();
    } catch (error) {
      console.error("Dosya indirilemedi: ", error);

      // Hata durumunda kullanıcıya bilgi ver
      Swal.fire({
        icon: "error",
        title: "Hata!",
        text: "Dosya indirilirken bir hata oluştu.",
        confirmButtonColor: "#3f51b5",
      });
    }
  };
  const renderEditButtons = (field) =>
    editMode[field] ? (
      <Stack direction="row" spacing={1}>
        <Tooltip title="Kaydet">
          <ActionButton
            color="primary"
            onClick={() => saveField(field)}
            size="small"
          >
            <CheckCircleRoundedIcon />
          </ActionButton>
        </Tooltip>
        <Tooltip title="İptal">
          <ActionButton
            color="error"
            onClick={() => cancelEdit(field)}
            size="small"
          >
            <CancelRoundedIcon />
          </ActionButton>
        </Tooltip>
      </Stack>
    ) : (
      <Tooltip title="Düzenle">
        <ActionButton
          color="primary"
          onClick={() => startEdit(field)}
          size="small"
        >
          <ModeEditIcon />
        </ActionButton>
      </Tooltip>
    );

  return (
    <StyledModal
      open={isOpen}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
        setEditMode({
          title: false,
          description: false,
          status: false,
          workerTitle: false,
          workType: false,
          taxpayer: false,
          expiresAt: false,
          files: false,
        });
      }}
      closeAfterTransition
    >
      <Fade in={isOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: "80vw", md: "60vw", lg: "50vw" },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 3,
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, bgcolor: "#f5f5f5" }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary.main"
              mb={2}
            >
              Not Detayları
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Mükellef */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <FieldLabel>Mükellef:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.taxpayer ? (
                    <Autocomplete
                      fullWidth
                      value={
                        taxpayerList.find((item) => item.id === tempTaxpayer) ||
                        null
                      }
                      onChange={(event, newValue) =>
                        setTempTaxpayer(newValue?.id || null)
                      }
                      options={
                        [{ id: "OTHER", title: "Diğer" }, ...taxpayerList] || []
                      }
                      getOptionLabel={(option) => option?.title || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: (theme) =>
                                theme.palette.grey[50],
                              border: (theme) =>
                                `0px solid ${theme.palette.grey[300]}`,
                              fontSize: "0.85rem",
                              padding: (theme) => theme.spacing(0.5, 1.5),
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.grey[100],
                                borderColor: (theme) =>
                                  theme.palette.primary.light,
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: 0,
                            },
                          }}
                        />
                      )}
                    />
                  ) : (
                    <ContentField>
                      {taxpayerList.find((item) => item.id === tempTaxpayer)
                        ?.title || "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("taxpayer")}
              </Stack>

              {/* Başlık */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <FieldLabel>Başlık:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.title ? (
                    <TextField
                      value={tempTitle}
                      onChange={(e) => setTempTitle(e.target.value)}
                      multiline
                      fullWidth
                      rows={3}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <ContentField
                      sx={{ height: "60px", alignItems: "flex-start" }}
                    >
                      {tempTitle || "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("title")}
              </Stack>

              {/* Açıklama */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <FieldLabel>Açıklama:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.description ? (
                    <TextField
                      value={tempDescription}
                      onChange={(e) => setTempDescription(e.target.value)}
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                    />
                  ) : (
                    <ContentField
                      sx={{ height: "100px", alignItems: "flex-start" }}
                    >
                      {tempDescription || "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("description")}
              </Stack>
              {/* File Upload Section */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <FieldLabel>Dosyalar:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.files ? (
                    <>
                      <input {...getInputProps()} />
                      <IconButton
                        color="primary"
                        onClick={open}
                        size="small"
                        sx={{
                          bgcolor: "#e3f2fd",
                          "&:hover": { bgcolor: "#bbdefb" },
                        }}
                      >
                        <AttachFileIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        Dosya Ekle
                      </Typography>
                      {tempFiles.length > 0 && (
                        <List
                          dense
                          sx={{ bgcolor: "white", borderRadius: 1, mt: 1 }}
                        >
                          {tempFiles.map((file, index) => (
                            <ListItem
                              key={index}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  onClick={() => handleDeleteFile(index)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <UploadFileIcon color="primary" sx={{ mr: 1 }} />
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => handleFileClick(file)}
                                  >
                                    {file.name}
                                  </Typography>
                                }
                                primaryTypographyProps={{
                                  style: {
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "300px",
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  ) : (
                    <ContentField
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        height: "auto",
                      }}
                    >
                      {tempFiles.length > 0
                        ? tempFiles.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                mb: 0.5,
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ display: "flex" }}>
                                {getFileIcon(file.name)}
                                <Typography variant="body2">
                                  {file.name}
                                </Typography>
                              </div>
                              <div>
                                {file?.driveId ? (
                                  <FileDownloadIcon
                                    color="primary"
                                    sx={{ ml: 1 }}
                                    onClick={() => {
                                      if (file?.driveId) {
                                        handleFileClick(file.driveId);
                                      } else {
                                        Swal.fire(
                                          "Hata!",
                                          "Dosyayı henüz yüklemediniz.",
                                          "error"
                                        );
                                      }
                                    }}
                                  />
                                ) : (
                                  ""
                                )}
                              </div>
                            </Box>
                          ))
                        : "—"}
                    </ContentField>
                  )}
                  <Collapse in={errorOpen}>
                    <Alert
                      severity="error"
                      onClose={() => setErrorOpen(false)}
                      sx={{ mt: 2 }}
                    >
                      7 MB'den büyük dosyalar yüklenemez!
                    </Alert>
                  </Collapse>
                </Box>
                {renderEditButtons("files")}
              </Stack>
              {/* Durum */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <FieldLabel>Durum:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.status ? (
                    <Autocomplete
                      fullWidth
                      value={
                        workStatusOptions.find(
                          (item) => item.id === tempStatus
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setTempStatus(newValue?.id || null)
                      }
                      options={workStatusOptions || []}
                      getOptionLabel={(option) => option?.title || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: (theme) =>
                                theme.palette.grey[50],
                              border: (theme) =>
                                `0px solid ${theme.palette.grey[300]}`,
                              fontSize: "0.85rem",
                              padding: (theme) => theme.spacing(0.5, 1.5),
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.grey[100],
                                borderColor: (theme) =>
                                  theme.palette.primary.light,
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: 0,
                            },
                          }}
                        />
                      )}
                    />
                  ) : (
                    <ContentField sx={{ padding: "5px 8px" }}>
                      <Chip
                        label={
                          workStatusOptions.find(
                            (option) => option.id === tempStatus
                          )?.title || "Bilinmeyen"
                        }
                        sx={(theme) => ({
                          backgroundColor: theme.palette.grey[50],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          borderRadius: theme.shape.borderRadius,
                          fontSize: "0.85rem",
                          height: "auto",
                          padding: theme.spacing(0.5, 1.5),
                          "& .MuiChip-label": {
                            padding: 0,
                          },
                          "&:hover": {
                            backgroundColor: theme.palette.grey[100],
                            borderColor: theme.palette.primary.light,
                          },
                        })}
                      />
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("status")}
              </Stack>

              {/* İş Türü */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <FieldLabel>İş Türü:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.workType ? (
                    <Autocomplete
                      fullWidth
                      value={
                        workTypeOptions.find(
                          (item) => item.id === tempWorkType
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setTempWorkType(newValue?.id || null)
                      }
                      options={workTypeOptions || []}
                      getOptionLabel={(option) => option?.title || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: (theme) =>
                                theme.palette.grey[50],
                              border: (theme) =>
                                `0px solid ${theme.palette.grey[300]}`,
                              fontSize: "0.85rem",
                              padding: (theme) => theme.spacing(0.5, 1.5),
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.grey[100],
                                borderColor: (theme) =>
                                  theme.palette.primary.light,
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: 0,
                            },
                          }}
                        />
                      )}
                    />
                  ) : (
                    <ContentField>
                      {workTypeOptions.find((item) => item.id === tempWorkType)
                        ?.title || "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("workType")}
              </Stack>

              {/* Sorumlu Kişi */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <FieldLabel>Sorumlu:</FieldLabel>
                <Box sx={{ flexGrow: 1 }}>
                  {editMode.workerTitle ? (
                    <Autocomplete
                      value={
                        workerList.find(
                          (item) => item.id === tempWorkerTitle
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setTempWorkerTitle(newValue?.id || null)
                      }
                      options={workerList || []}
                      getOptionLabel={(option) => option?.title || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: (theme) =>
                                theme.palette.grey[50],
                              border: (theme) =>
                                `0px solid ${theme.palette.grey[300]}`,
                              fontSize: "0.85rem",
                              padding: (theme) => theme.spacing(0.5, 1.5),
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.grey[100],
                                borderColor: (theme) =>
                                  theme.palette.primary.light,
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: 0,
                            },
                          }}
                        />
                      )}
                    />
                  ) : (
                    <ContentField>
                      {workerList.find((item) => item.id === tempWorkerTitle)
                        ?.title || "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("workerTitle")}
              </Stack>

              {/* Bitiş Tarihi */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <FieldLabel>Bitiş Tarihi:</FieldLabel>
                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: "hidden",
                  }}
                >
                  {editMode.expiresAt ? (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <StyledDatePicker
                        value={tempExpiresAt}
                        onChange={(newValue) => setTempExpiresAt(newValue)}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  ) : (
                    <ContentField>
                      {tempExpiresAt
                        ? moment(tempExpiresAt).format("DD/MM/YYYY")
                        : "—"}
                    </ContentField>
                  )}
                </Box>
                {renderEditButtons("expiresAt")}
              </Stack>
            </Stack>

            {/* Actions */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-around" }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={onClose}
                sx={{
                  px: 2,
                  py: 0.7,
                  borderRadius: "6px", // yumuşak köşeler
                  textTransform: "none", // Büyük harfleri kaldır
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // soft gölge
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Kapat
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<CheckCircleRoundedIcon />}
                sx={{
                  backgroundColor: "#06990b",
                  px: 2,
                  py: 0.7,
                  borderRadius: "6px", // yumuşak köşeler
                  textTransform: "none", // Büyük harfleri kaldır
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // soft gölge
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Değişiklikleri Kaydet
              </Button>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </StyledModal>
  );
};
